import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, money, statusBadge, priorityBadge, categoryColor } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import ClientPortal from "./ClientPortal";
import { propColor } from "./TimeClock";
import AlertsWidget from "../components/AlertsWidget";
import QuoteApprovalsWidget from "../components/QuoteApprovalsWidget";
import {
  AreaChart, Area, PieChart, Pie, Cell, ResponsiveContainer, XAxis, Tooltip,
} from "recharts";
import {
  DollarSign, Wrench, Banknote, CalendarClock, Clock, TrendingUp, TrendingDown,
  HardHat, Receipt, Plus, ArrowRight, CheckCircle2, Radio, MapPin, Building2, MessageSquare, Check, X, ShoppingCart,
} from "lucide-react";

const STATUS_COLORS = {
  to_schedule: "#d97706", to_do: "#0ea5e9", make_estimate: "#6366f1", estimate_sent: "#2563eb",
  estimate_approved: "#059669", in_progress: "#0891b2", waiting_info: "#ea580c", job_done: "#0d9488",
  completed: "#16a34a", completed_no_charge: "#65a30d", blocked: "#dc2626", cancelled: "#94a3b8",
  estimate_rejected: "#e11d48",
};

const card = "bg-white rounded-2xl shadow-sm border border-slate-100 p-5 transition-shadow hover:shadow-md";

const Delta = ({ value }) => {
  const up = value >= 0;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold ${up ? "text-green-600" : "text-red-500"}`}>
      {up ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
      {up ? "+" : ""}{value}%
    </span>
  );
};

const KpiCard = ({ icon: Icon, label, value, delta, sub, chip, testid }) => (
  <div data-testid={testid} className={card}>
    <div className="flex items-start justify-between">
      <span className="text-xs font-medium text-slate-500 leading-tight">{label}</span>
      <span className={`w-9 h-9 shrink-0 rounded-full flex items-center justify-center ${chip}`}><Icon className="w-4 h-4" /></span>
    </div>
    <div className="mt-2 text-2xl font-bold tracking-tight text-slate-900 tabular-nums">{value}</div>
    <div className="mt-1">
      {delta !== undefined ? <><Delta value={delta} /> <span className="text-[11px] text-slate-400">{sub}</span></>
        : <span className="text-[11px] text-slate-400">{sub}</span>}
    </div>
  </div>
);

const RevenueTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  return <div className="bg-slate-900 text-white text-xs rounded-lg px-2.5 py-1.5 shadow-lg">{money(payload[0].value)}</div>;
};

export default function Dashboard() {
  const { t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [overview, setOverview] = useState(null);
  const [insights, setInsights] = useState(null);
  const [active, setActive] = useState(null);
  const [pendingReqs, setPendingReqs] = useState([]);
  const [pendingPOs, setPendingPOs] = useState([]);
  const isManager = ["admin", "manager"].includes(user.role);
  const isPurchasing = user.role === "purchasing";
  const canSeeFinance = ["admin", "manager", "financeiro", "director"].includes(user.role);

  const loadClockins = () => {
    api.get("/timeclock/active").then(setActive).catch(() => {});
    api.get("/time-requests?status=pending").then(setPendingReqs).catch(() => {});
  };

  useEffect(() => {
    if (user.role === "client") return;
    api.get("/dashboard/stats").then(setStats).catch(() => {});
    if (isManager) {
      api.get("/dashboard/overview").then(setOverview).catch(() => {});
      api.get("/dashboard/insights").then(setInsights).catch(() => {});
      loadClockins();
      const iv = setInterval(loadClockins, 30000);
      return () => clearInterval(iv);
    }
    if (isPurchasing) {
      api.get("/purchase-orders").then((pos) => {
        setPendingPOs((pos || []).filter((po) => (po.items || []).some((i) => !i.purchased) || po.status !== "delivered"));
      }).catch(() => {});
    }
  }, []); // eslint-disable-line

  const resolveReq = async (id, status) => {
    try {
      await api.put(`/time-requests/${id}`, { status });
      toast.success(status === "approved" ? t("approved") : t("rejected"));
      loadClockins();
    } catch { toast.error(t("error")); }
  };

  if (user.role === "client") return <ClientPortal />;

  if (!stats) return <div className="text-slate-400 text-sm">Loading…</div>;

  // Status donut data
  const donut = Object.entries(stats.by_status)
    .filter(([, v]) => v > 0)
    .map(([k, v]) => ({ key: k, name: t(k === "completed" ? "completed_s" : k), value: v, color: STATUS_COLORS[k] || "#94a3b8" }))
    .sort((a, b) => b.value - a.value);
  const donutTotal = donut.reduce((s, d) => s + d.value, 0);

  const k = overview?.kpis;
  const kpis = isManager && k ? [
    { icon: DollarSign, chip: "bg-green-50 text-green-600", label: t("receivedThisMonth"), value: money(k.received_month), delta: k.received_delta, sub: t("vsLastMonth"), testid: "kpi-received" },
    { icon: Banknote, chip: "bg-orange-50 text-orange-600", label: t("toReceive"), value: money(k.to_receive), sub: `${t("pendingValue")}: ${money(k.pending_value)}`, testid: "kpi-toreceive" },
    { icon: Wrench, chip: "bg-blue-50 text-blue-600", label: t("openTickets"), value: k.open_calls, delta: k.new_tickets_delta, sub: `${k.new_tickets_month} ${t("newThisMonth").toLowerCase()}`, testid: "kpi-open" },
    { icon: CalendarClock, chip: "bg-indigo-50 text-indigo-600", label: t("scheduledThisWeek"), value: k.scheduled_week, sub: t("upcomingSchedule"), testid: "kpi-scheduled" },
    { icon: TrendingUp, chip: "bg-violet-50 text-violet-600", label: t("approvedValue"), value: money(stats.approved_value), sub: t("pendingValue"), testid: "kpi-approved" },
  ] : [
    { icon: Wrench, chip: "bg-blue-50 text-blue-600", label: t("openTickets"), value: stats.by_status.to_schedule + stats.by_status.to_do, sub: t("byStatus"), testid: "kpi-open" },
    { icon: Clock, chip: "bg-cyan-50 text-cyan-600", label: t("inProgress"), value: stats.by_status.in_progress, sub: "", testid: "kpi-inprogress" },
    { icon: CheckCircle2, chip: "bg-green-50 text-green-600", label: t("completed"), value: stats.by_status.completed, sub: "", testid: "kpi-completed" },
    { icon: Banknote, chip: "bg-orange-50 text-orange-600", label: t("properties"), value: stats.properties_count, sub: "", testid: "kpi-properties" },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">{t("dashboard")}</h1>
          <p className="text-slate-500 text-sm mt-0.5">{user.name} · <span className="capitalize">{user.role}</span></p>
        </div>
        {isManager && (
          <button data-testid="new-call-btn" onClick={() => navigate("/tickets")} className="inline-flex items-center gap-2 rounded-full bg-orange-600 text-white text-sm font-semibold px-4 py-2.5 hover:bg-orange-700 transition-colors shadow-sm">
            <Plus className="w-4 h-4" /> {t("newServiceCall")}
          </button>
        )}
      </div>

      {/* KPI row */}
      <div className={`grid grid-cols-2 ${isManager ? "lg:grid-cols-5" : "lg:grid-cols-4"} gap-4`}>
        {kpis.map((m, i) => <KpiCard key={i} {...m} />)}
      </div>

      {isManager && <AlertsWidget />}
      {canSeeFinance && <FinanceSummaryWidget />}
      <QuoteApprovalsWidget />

      {/* Purchasing: services with pending purchases */}
      {isPurchasing && (
        <div className={`${card} !p-0 overflow-hidden`} data-testid="pending-purchases">
          <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between">
            <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2"><ShoppingCart className="w-4 h-4 text-amber-500" />{t("pendingPurchases")}</h2>
            <button onClick={() => navigate("/purchasing")} className="text-xs text-orange-600 font-semibold inline-flex items-center gap-1 hover:gap-1.5 transition-all">{t("view")} <ArrowRight className="w-3.5 h-3.5" /></button>
          </div>
          <div className="divide-y divide-slate-50 max-h-96 overflow-y-auto">
            {pendingPOs.length === 0 && <div className="px-5 py-8 text-center text-slate-400 text-sm">{t("noPendingPurchases")}</div>}
            {pendingPOs.map((po) => {
              const bought = (po.items || []).filter((i) => i.purchased).length;
              const total = (po.items || []).length;
              const remainingEst = (po.items || []).reduce((s, i) => s + (i.purchased ? 0 : (i.estimated_unit_price ?? i.unit_price ?? 0) * (i.quantity ?? 1)), 0);
              return (
                <div key={po.id} data-testid={`pending-po-${po.id}`} onClick={() => navigate("/purchasing")} className="px-5 py-3 flex items-center gap-3 cursor-pointer hover:bg-slate-50">
                  <span className="font-mono text-[11px] text-slate-400 w-24 shrink-0">{po.number}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate">{po.title}</div>
                    <div className="text-[11px] text-slate-400">{po.unit_name}</div>
                  </div>
                  <span className="text-xs text-slate-500 tabular-nums shrink-0">{bought}/{total} {t("materials").toLowerCase()}</span>
                  <span className="text-sm font-bold text-amber-600 tabular-nums w-20 text-right shrink-0">{money(remainingEst)}</span>
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium border shrink-0 ${statusBadge(po.status)}`}>{t(po.status)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Live clock-ins ("Batidos agora") + pending time requests */}
      {isManager && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className={`${card} lg:col-span-2 !p-0 overflow-hidden`} data-testid="clocked-in-now">
            <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between">
              <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2">
                <span className="relative flex h-2.5 w-2.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" /><span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" /></span>
                {t("clockedInNow")}
              </h2>
              <span className="text-xs font-bold text-slate-500 tabular-nums">{active?.count ?? 0} {t("online")}</span>
            </div>
            <div className="divide-y divide-slate-50 max-h-72 overflow-y-auto">
              {(active?.active || []).length === 0 && <div className="px-5 py-8 text-center text-slate-400 text-sm">{t("nobodyClockedIn")}</div>}
              {(active?.active || []).map((a) => {
                const pc = a.current_job ? propColor(a.current_job.unit_name) : null;
                const since = a.job_since || a.shift_since;
                return (
                  <div key={a.tech_id} data-testid={`active-tech-${a.tech_id}`} className="flex items-center gap-3 px-5 py-3">
                    <span className="w-9 h-9 shrink-0 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-sm font-bold">{(a.tech_name || "?").slice(0, 1).toUpperCase()}</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-slate-900 truncate">{a.tech_name}</div>
                      {a.current_job ? (
                        <span className={`inline-flex items-center gap-1 mt-0.5 text-[11px] font-semibold px-2 py-0.5 rounded-full ${pc.bg} ${pc.text}`}>
                          <Building2 className="w-3 h-3" />{a.current_job.unit_name || a.current_job.title}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 mt-0.5 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">{t("officeStandby")}</span>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-xs text-slate-500">{t("since")} {since ? new Date(since).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"}</div>
                      <div className="text-xs font-bold text-slate-700 tabular-nums">{(a.job_hours_today || 0).toFixed(2)}h {t("today").toLowerCase()}</div>
                    </div>
                    {a.location && <a href={`https://maps.google.com/?q=${a.location.lat},${a.location.lng}`} target="_blank" rel="noreferrer" className="text-blue-500 shrink-0"><MapPin className="w-4 h-4" /></a>}
                  </div>
                );
              })}
            </div>
          </div>

          <div className={`${card} !p-0 overflow-hidden`} data-testid="pending-requests">
            <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between">
              <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2"><MessageSquare className="w-4 h-4 text-slate-400" />{t("timeRequests")}</h2>
              <span className="text-xs font-bold text-amber-600 tabular-nums">{pendingReqs.length}</span>
            </div>
            <div className="divide-y divide-slate-50 max-h-72 overflow-y-auto">
              {pendingReqs.length === 0 && <div className="px-5 py-8 text-center text-slate-400 text-sm">{t("noRequests")}</div>}
              {pendingReqs.map((r) => (
                <div key={r.id} data-testid={`pending-req-${r.id}`} className="px-5 py-3">
                  <div className="text-sm font-semibold text-slate-900">{r.tech_name} <span className="text-xs font-normal text-slate-400">· {r.date}</span></div>
                  <div className="text-xs text-slate-600 mt-0.5 mb-2">{r.message}</div>
                  <div className="flex gap-2">
                    <button data-testid={`req-approve-${r.id}`} onClick={() => resolveReq(r.id, "approved")} className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 text-white text-xs font-semibold px-2.5 py-1.5 hover:bg-emerald-700"><Check className="w-3.5 h-3.5" />{t("approve")}</button>
                    <button data-testid={`req-reject-${r.id}`} onClick={() => resolveReq(r.id, "rejected")} className="inline-flex items-center gap-1 rounded-lg border border-slate-300 text-slate-600 text-xs font-semibold px-2.5 py-1.5 hover:bg-slate-50"><X className="w-3.5 h-3.5" />{t("reject")}</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Middle row: trend + donut + subs */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-5">
        {/* Revenue trend */}
        {isManager && (
        <div className={`${card} lg:col-span-2`} data-testid="revenue-trend">
          <div className="flex items-start justify-between mb-2">
            <div>
              <h2 className="text-base font-semibold text-slate-900">{t("revenueReceived")}</h2>
              <div className="text-2xl font-bold text-slate-900 mt-1 tabular-nums">{money(overview?.received_month_total || 0)}</div>
            </div>
            {isManager && overview && (
              <div className="flex gap-4 text-right">
                <div><div className="text-[10px] uppercase font-bold text-slate-400">{t("avgDay")}</div><div className="text-sm font-bold text-slate-700 tabular-nums">{money(overview.avg_day)}</div></div>
                <div><div className="text-[10px] uppercase font-bold text-slate-400">{t("bestDay")}</div><div className="text-sm font-bold text-slate-700 tabular-nums">{money(overview.best_day)}</div></div>
              </div>
            )}
          </div>
          <div className="h-44">
            <ResponsiveContainer width="100%" height="100%" minHeight={140}>
              <AreaChart data={overview?.revenue_trend || []} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#16a34a" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#16a34a" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} interval={4} />
                <Tooltip content={<RevenueTooltip />} />
                <Area type="monotone" dataKey="value" stroke="#16a34a" strokeWidth={2.5} fill="url(#revGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
        )}

        {/* Status donut */}
        <div className={isManager ? card : `${card} lg:col-span-2`} data-testid="status-donut">
          <h2 className="text-base font-semibold text-slate-900">{t("leadsByStatus")}</h2>
          <p className="text-xs text-slate-400 mb-2">{donutTotal} {t("recentTickets").toLowerCase()}</p>
          <div className="relative h-40">
            <ResponsiveContainer width="100%" height="100%" minHeight={140}>
              <PieChart>
                <Pie data={donut} dataKey="value" nameKey="name" innerRadius={48} outerRadius={70} paddingAngle={2} stroke="none">
                  {donut.map((d, i) => <Cell key={i} fill={d.color} />)}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <span className="text-[10px] uppercase font-bold text-slate-400">{t("totalLabel")}</span>
              <span className="text-2xl font-bold text-slate-900">{donutTotal}</span>
            </div>
          </div>
          <div className="mt-2 space-y-1 max-h-28 overflow-y-auto">
            {donut.slice(0, 6).map((d) => (
              <div key={d.key} className="flex items-center justify-between text-xs">
                <span className="flex items-center gap-1.5 text-slate-600"><span className="w-2.5 h-2.5 rounded-full" style={{ background: d.color }} />{d.name}</span>
                <span className="text-slate-400 tabular-nums">{d.value} ({Math.round(d.value / donutTotal * 100)}%)</span>
              </div>
            ))}
          </div>
        </div>

        {/* Top subcontractors */}
        {isManager && (
        <div className={card} data-testid="top-subs">
          <h2 className="text-base font-semibold text-slate-900 mb-3">{t("topSubcontractors")}</h2>
          {(overview?.top_subcontractors || []).length === 0 ? (
            <p className="text-sm text-slate-400 py-8 text-center">{t("noData")}</p>
          ) : (
            <div className="space-y-1">
              {overview.top_subcontractors.map((s, i) => (
                <button key={i} data-testid={`top-sub-${i}`} onClick={() => navigate("/payments")} className="w-full flex items-center gap-3 py-2 text-left hover:bg-slate-50 -mx-2 px-2 rounded-lg transition-colors">
                  <span className="w-7 h-7 shrink-0 rounded-full bg-orange-100 text-orange-700 flex items-center justify-center text-xs font-bold">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate">{s.name}</div>
                    <div className="text-[11px] text-slate-400">{s.jobs} {t("jobsCount")}</div>
                  </div>
                  <span className="text-sm font-bold text-slate-900 tabular-nums">{money(s.earned)}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        )}
      </div>

      {/* Bottom row: recent calls + schedule + receivables */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Recent service calls */}
        <div className={`${card} !p-0 overflow-hidden ${isManager ? "" : "lg:col-span-3"}`}>
          <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between">
            <h2 className="text-base font-semibold text-slate-900">{t("recentTickets")}</h2>
            <button onClick={() => navigate("/tickets")} className="text-xs text-orange-600 font-semibold inline-flex items-center gap-1 hover:gap-1.5 transition-all">{t("view")} <ArrowRight className="w-3.5 h-3.5" /></button>
          </div>
          <div className="divide-y divide-slate-50">
            {stats.recent_tickets.length === 0 && <div className="px-5 py-8 text-center text-slate-400 text-sm">{t("noData")}</div>}
            {stats.recent_tickets.slice(0, 5).map((tk) => (
              <button key={tk.id} data-testid={`recent-ticket-${tk.id}`} onClick={() => navigate("/tickets")} className="w-full flex items-center gap-3 px-5 py-2.5 hover:bg-slate-50 text-left transition-colors">
                <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${categoryColor(tk.category)}`}>{t(tk.category)}</span>
                <span className="flex-1 text-sm font-medium text-slate-800 truncate">{tk.title}</span>
                <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium border ${priorityBadge(tk.priority)}`}>{t(tk.priority)}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Upcoming schedule */}
        {isManager && (
        <div className={`${card} !p-0 overflow-hidden`}>
          <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between">
            <h2 className="text-base font-semibold text-slate-900">{t("upcomingSchedule")}</h2>
            <button onClick={() => navigate("/schedule")} className="text-xs text-orange-600 font-semibold inline-flex items-center gap-1 hover:gap-1.5 transition-all">{t("view")} <ArrowRight className="w-3.5 h-3.5" /></button>
          </div>
          <div className="divide-y divide-slate-50">
            {(overview?.upcoming || []).length === 0 && <div className="px-5 py-8 text-center text-slate-400 text-sm">{t("nothingScheduled")}</div>}
            {(overview?.upcoming || []).map((u, i) => (
              <div key={i} data-testid={`upcoming-${i}`} className="flex items-center gap-3 px-5 py-2.5">
                <span className="w-1 self-stretch rounded-full" style={{ background: STATUS_COLORS.in_progress }} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-800 truncate">{u.title}</div>
                  <div className="text-[11px] text-slate-400 truncate">{u.unit_name}{u.assigned_to_name ? ` · ${u.assigned_to_name}` : ""}</div>
                </div>
                <span className="text-xs font-medium text-slate-500 tabular-nums shrink-0">{(u.scheduled_date || "").slice(5, 10)}</span>
              </div>
            ))}
          </div>
        </div>
        )}

        {/* Receivables */}
        {isManager && (
        <div className={`${card} !p-0 overflow-hidden`}>
          <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between">
            <h2 className="text-base font-semibold text-slate-900">{t("receivables")}</h2>
            <button onClick={() => navigate("/receivables")} className="text-xs text-orange-600 font-semibold inline-flex items-center gap-1 hover:gap-1.5 transition-all">{t("view")} <ArrowRight className="w-3.5 h-3.5" /></button>
          </div>
          <div className="divide-y divide-slate-50">
            {(overview?.recent_receivables || []).length === 0 && <div className="px-5 py-8 text-center text-slate-400 text-sm">{t("noData")}</div>}
            {(overview?.recent_receivables || []).map((r, i) => (
              <button key={i} data-testid={`recv-${i}`} onClick={() => navigate("/receivables")} className="w-full flex items-center gap-3 px-5 py-2.5 hover:bg-slate-50 text-left transition-colors">
                <Receipt className="w-4 h-4 text-slate-300 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-800 truncate">{r.title}</div>
                  <div className="text-[11px] text-slate-400 font-mono">{r.number}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-sm font-bold text-slate-900 tabular-nums">{money(r.total)}</div>
                  <span className={`inline-flex px-1.5 py-0.5 rounded-full text-[9px] font-medium border ${statusBadge(r.status)}`}>{t(r.status)}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
        )}
      </div>

      {/* Manager insights: over budget + team hours */}
      {isManager && insights && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div data-testid="insight-over-budget" className={card}>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base font-semibold text-slate-900">{t("topOverBudget")}</h2>
              <button onClick={() => navigate("/financials")} className="text-xs text-orange-600 font-semibold">{t("view")} →</button>
            </div>
            {insights.top_over_budget.length === 0 ? (
              <p className="text-sm text-emerald-600 py-6 text-center">{t("noOverBudget")}</p>
            ) : (
              <div className="divide-y divide-slate-50">
                {insights.top_over_budget.map((p, i) => (
                  <button key={i} data-testid={`over-budget-${i}`} onClick={() => navigate("/financials")} className="w-full flex items-center justify-between py-2.5 text-left hover:bg-slate-50 -mx-2 px-2 rounded-lg">
                    <div>
                      <div className="font-medium text-slate-900 text-sm">{p.property_name}</div>
                      <div className="text-xs text-slate-400">{t("budget")}: {money(p.budget_total)} · {t("real")}: {money(p.actual_total)}</div>
                    </div>
                    <div className="text-sm font-bold text-red-600">+{money(p.overrun)}</div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div data-testid="insight-team-hours" className={card}>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base font-semibold text-slate-900">{t("teamHoursWeek")}</h2>
              <span className="text-xs text-slate-400">{t("thisWeekTotal")}: <b className="text-slate-700">{insights.team_hours_total}h</b></span>
            </div>
            {insights.team_hours_week.length === 0 ? (
              <p className="text-sm text-slate-400 py-6 text-center">{t("noData")}</p>
            ) : (
              <div className="space-y-2">
                {insights.team_hours_week.map((g, i) => {
                  const max = Math.max(...insights.team_hours_week.map((x) => x.hours), 1);
                  return (
                    <div key={i} data-testid={`team-hours-${i}`} className="flex items-center gap-3">
                      <span className="w-28 shrink-0 text-xs font-medium text-slate-600 truncate">{g.tech_name}</span>
                      <div className="flex-1 h-4 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-orange-500 rounded-full transition-all duration-500" style={{ width: `${Math.max((g.hours / max) * 100, g.hours > 0 ? 4 : 0)}%` }} />
                      </div>
                      <span className="w-12 shrink-0 text-right text-sm font-semibold text-slate-900 tabular-nums">{g.hours}h</span>
                      <span className="w-16 shrink-0 text-right text-xs text-slate-400 tabular-nums">{money(g.cost)}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Labor cost by property this week */}
      {isManager && insights && (
        <div className={`${card} !p-0 overflow-hidden`} data-testid="labor-by-property">
          <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between">
            <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2"><HardHat className="w-4 h-4 text-slate-400" />{t("laborByProperty")}</h2>
            <button onClick={() => navigate("/financials")} className="text-xs text-orange-600 font-semibold inline-flex items-center gap-1 hover:gap-1.5 transition-all">{t("view")} <ArrowRight className="w-3.5 h-3.5" /></button>
          </div>
          <div className="p-5">
            {(insights.labor_by_property || []).length === 0 ? (
              <p className="text-sm text-slate-400 py-6 text-center">{t("noData")}</p>
            ) : (
              <div className="space-y-2.5">
                {insights.labor_by_property.map((p, i) => {
                  const max = Math.max(...insights.labor_by_property.map((x) => x.cost), 1);
                  const pc = propColor(p.property_name);
                  return (
                    <div key={i} data-testid={`labor-prop-${i}`} className="flex items-center gap-3">
                      <span className={`inline-flex items-center gap-1.5 w-44 shrink-0 text-[11px] font-semibold px-2 py-1 rounded-full truncate ${pc.bg} ${pc.text}`}>
                        <Building2 className="w-3 h-3 shrink-0" /><span className="truncate">{p.property_name}</span>
                      </span>
                      <div className="flex-1 h-4 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${Math.max((p.cost / max) * 100, p.cost > 0 ? 4 : 0)}%`, background: pc.dot }} />
                      </div>
                      <span className="w-14 shrink-0 text-right text-xs text-slate-500 tabular-nums">{p.hours}h</span>
                      <span className="w-20 shrink-0 text-right text-sm font-bold text-slate-900 tabular-nums">{money(p.cost)}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// -------------------- Finance summary widget (dashboard) --------------------
// Visible only to roles ["admin","manager","financeiro","director"] — shows
// account balances at a glance plus a small period cash-flow (last 30 days).
function FinanceSummaryWidget() {
  const navigate = useNavigate();
  const [data, setData] = React.useState(null);
  React.useEffect(() => {
    const start = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
    api.get(`/finance/summary?start=${start}`).then(setData).catch(() => setData(null));
  }, []);
  if (!data) return null;
  const lowAcc = (data.accounts || []).filter((a) => a.type !== "credit_card" && (a.current_balance || 0) < 1000);
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 md:p-5" data-testid="dashboard-finance-widget">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Finance · Last 30 days</div>
          <div className="text-lg font-bold text-slate-900">Cash flow</div>
        </div>
        <button onClick={() => navigate("/finance")} data-testid="dashboard-finance-open" className="text-xs font-semibold text-slate-600 hover:text-slate-900 inline-flex items-center gap-1">Open cash book →</button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
        <div className="p-2 rounded-lg bg-slate-50 border border-slate-100">
          <div className="text-[9px] font-bold uppercase text-slate-400">Total balance</div>
          <div className="text-base font-bold tabular-nums text-slate-900">{money(data.total_balance)}</div>
        </div>
        <div className="p-2 rounded-lg bg-emerald-50 border border-emerald-100">
          <div className="text-[9px] font-bold uppercase text-emerald-700">Cash in</div>
          <div className="text-base font-bold tabular-nums text-emerald-700">{money(data.cash_in)}</div>
        </div>
        <div className="p-2 rounded-lg bg-rose-50 border border-rose-100">
          <div className="text-[9px] font-bold uppercase text-rose-700">Cash out</div>
          <div className="text-base font-bold tabular-nums text-rose-700">{money(data.cash_out)}</div>
        </div>
        <div className={`p-2 rounded-lg border ${data.net >= 0 ? "bg-emerald-50 border-emerald-100" : "bg-rose-50 border-rose-100"}`}>
          <div className={`text-[9px] font-bold uppercase ${data.net >= 0 ? "text-emerald-700" : "text-rose-700"}`}>Net</div>
          <div className={`text-base font-bold tabular-nums ${data.net >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{money(data.net)}</div>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
        {(data.accounts || []).map((a) => (
          <div key={a.id} className="p-2 border border-slate-100 rounded-lg">
            <div className="text-[9px] font-bold uppercase text-slate-400 truncate">{a.name}</div>
            <div className={`text-sm font-bold tabular-nums ${(a.current_balance || 0) < 0 ? "text-rose-700" : "text-slate-800"}`}>{money(a.current_balance || 0)}</div>
            <div className="text-[9px] text-slate-400 truncate">{a.type}{a.account_last4 ? ` ****${a.account_last4}` : ""}</div>
          </div>
        ))}
        {(data.accounts || []).length === 0 && <div className="col-span-full text-xs text-slate-400 text-center py-3">No accounts yet — <button onClick={() => navigate("/finance")} className="underline">create your first account</button>.</div>}
      </div>
      {lowAcc.length > 0 && (
        <div className="mt-3 pt-3 border-t border-slate-100 text-[11px] text-amber-700 bg-amber-50 -mx-4 md:-mx-5 -mb-4 md:-mb-5 rounded-b-xl px-4 md:px-5 py-2 flex items-center gap-1.5">
          ⚠️ Low balance: {lowAcc.map((a) => a.name).join(", ")}
        </div>
      )}
    </div>
  );
}

